import { useRef, useState, useCallback } from "react";
import { showToast } from "../utils/toastUtil";

/**
 * useVoiceToText
 * Provides real-time (interim) voice-to-text for a single text field.
 *
 * @param {Function} setter  — setState updater for the target field value (string)
 * @returns { isListening, startListening, stopListening }
 */
const useVoiceToText = (setter) => {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  // Holds the current interim transcript so we can replace it on next result
  const interimRef = useRef("");

  const startListening = useCallback(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      showToast(
        "Voice input is not supported in this browser. Please use Chrome.",
        "ERROR",
      );
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true; // ← key change: real-time updates
    recognition.lang = "en-US";

    recognition.onresult = (event) => {
      let interim = "";
      let finalTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript.trim() + " ";
        } else {
          interim += result[0].transcript;
        }
      }

      setter((prev) => {
        // Strip the previous interim chunk, then append new interim/final
        const withoutPrevInterim = interimRef.current
          ? prev.endsWith(interimRef.current)
            ? prev.slice(0, prev.length - interimRef.current.length)
            : prev
          : prev;

        if (finalTranscript) {
          interimRef.current = "";
          const base = withoutPrevInterim.trimEnd();
          return base
            ? `${base} ${finalTranscript.trim()}`
            : finalTranscript.trim();
        }

        // Show interim live
        interimRef.current = interim;
        return withoutPrevInterim + interim;
      });
    };

    recognition.onerror = (event) => {
      if (event.error !== "aborted") {
        showToast("Voice recognition error. Please try again.", "ERROR");
      }
      interimRef.current = "";
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognition.onend = () => {
      interimRef.current = "";
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [setter]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) recognitionRef.current.stop();
  }, []);

  return { isListening, startListening, stopListening };
};

export default useVoiceToText;
